// Brand color tokens — single source of truth for all UI colors.
//
// Values are measured from the live reference site (lionbear.website/msgpdf)
// and mirror the "MSG to PDF Design System" tokens. The names are the site's
// own: seal (the burgundy primary), a desaturated slate-teal ramp, powder for
// every border, and paper for card/band fills over a white page.
//
// IMPORTANT: New UI code should import from here rather than inline hex
// literals. The CSS variables in src/index.css mirror these values — if you
// change one, change the other.
//
// There is deliberately NO `shadows` export. The reference site casts no
// box-shadow anywhere; depth comes from 1px powder borders and white/paper
// band alternation. Adding a shadow departs from the brand.

const page = '#FFFFFF';
const surface = '#FFFFFF';
const paper = '#FAF9F6';

const ink = '#17191A';

const teal900 = '#33474D';
const teal700 = '#4F6B73';
const teal500 = '#7096A0';

const powder = '#C3DEE0';

const seal = '#8F1F2A';
const sealDark = '#6E181F';
const gold = '#F0B93C';

// Two measured one-offs that don't belong to a ramp:
// `warmGray` is the muted cell in the comparison table (a competitor weakness);
// `goldInk` is the text inside the gold constraint chip — a dark amber, not ink.
const warmGray = '#B0ACA4';
const goldInk = '#8A5C0B';

// Derived, not measured — the reference site shows no tinted fills or status
// states. Error stays brighter and warmer than seal so a failure never reads
// as a brand-colored call to action.
const powderTint = '#EAF4F5';
const sealTint = '#F7E9EB';
const goldTint = '#FDF3DE';

export const colors = {
  // ─── Surfaces ────────────────────────────────────────────────────────────
  // Note the direction: the PAGE is white and PAPER is the card/band fill.
  page,
  surface,
  paper,
  surfaceOverlay: 'rgba(23, 25, 26, 0.45)',

  // ─── Ink ─────────────────────────────────────────────────────────────────
  ink,

  // ─── Slate-teal — body copy and the secondary accent (not a brand teal) ──
  teal900, teal700, teal500,

  // ─── Powder — the border color for everything ────────────────────────────
  powder,

  // ─── Seal — the one primary action color ─────────────────────────────────
  seal, sealDark,

  // ─── Gold — the "Max 10MB" constraint chip, and little else ──────────────
  gold,
  goldInk,

  // ─── Muted cell text in the comparison table ─────────────────────────────
  warmGray,

  // Zebra striping / table header fill. Neutral gray at 7% over white.
  zebra: 'rgba(128, 128, 128, 0.07)',

  // ─── Derived tints ───────────────────────────────────────────────────────
  powderTint, sealTint, goldTint,

  // ─── Semantic (derived) ──────────────────────────────────────────────────
  success700: '#1F6B47',
  success500: '#2F8F5B',
  success100: '#E4F2EA',
  error700: '#8E2A1F',
  error500: '#C0392B',
  error100: '#FBE7E4',
  warning500: gold,
  warning100: goldTint,

  // ─── Semantic roles (prefer these over raw ramp values) ──────────────────
  textPrimary: ink,
  textSecondary: teal900,
  textMuted: teal500,
  textInverse: '#FFFFFF',

  surfacePage: page,
  surfaceCard: paper,
  surfaceWell: paper,

  accentPrimary: seal,
  accentPrimaryHover: sealDark,
  accentPrimaryTint: sealTint,
  accentSecondary: teal700,
  accentSecondaryHover: teal900,
  accentSecondaryTint: powderTint,

  borderSubtle: powder,
  borderEmphasis: teal500,
};

// Measured per-component — these are not one geometric scale, so each is named
// for where it is used. Buttons are rounded rectangles; only chips are pills.
export const radii = {
  control: 8,
  button: 10,
  panel: 16,
  card: 18,
  dropzone: 20,
  pill: 999,
};

export const fonts = {
  display: "'Fraunces', Georgia, 'Times New Roman', serif",
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', 'Courier New', monospace",
};

// Minimal and functional only — no bounce, no page-load choreography.
export const motion = {
  easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  durationFast: '0.15s',
  durationBase: '0.25s',
};
